import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { timeAgo } from "@/components/community";

export function NotificationsBell({ variant = "desktop" }) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/notifications")
      .then(({ data }) => { setItems(data.items || []); setUnread(data.unread || 0); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const openItem = (n) => {
    setOpen(false);
    if (!n.read) {
      setItems((p) => p.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      api.post(`/notifications/${n.id}/read`).catch(() => {});
    }
    if (n.link) nav(n.link);
  };

  const markAll = () => {
    setUnread(0);
    setItems((p) => p.map((x) => ({ ...x, read: true })));
    api.post("/notifications/read").catch(() => {});
  };

  const badgePos = variant === "desktop" ? "-right-0.5 -top-0.5" : "right-0.5 top-0.5";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative rounded-lg p-2 hover:bg-secondary" aria-label="Notifications"
          data-testid={variant === "desktop" ? "header-notif" : "mobile-notif"}>
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notif-badge"
              className={`absolute ${badgePos} flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-white`}>
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-sm font-semibold">Notifications</span>
          {unread > 0 && (
            <button onClick={markAll} data-testid="notif-mark-all"
              className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">
              <CheckCheck className="h-3.5 w-3.5" />Mark all read
            </button>
          )}
        </div>
        <ScrollArea className="max-h-[22rem]">
          {items.length === 0 ? (
            <p className="px-3 py-10 text-center text-sm text-muted-foreground" data-testid="notif-empty">
              No notifications yet.
            </p>
          ) : (
            items.map((n) => (
              <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-item-${n.id}`}
                className={`flex w-full items-start gap-2 border-b border-border px-3 py-2.5 text-left transition-colors hover:bg-secondary/60 ${!n.read ? "bg-primary/5" : ""}`}>
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${!n.read ? "bg-accent" : "bg-transparent"}`} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium leading-snug text-foreground">{n.title}</span>
                  {n.body && <span className="mt-0.5 block truncate text-xs text-muted-foreground">{n.body}</span>}
                  <span className="mt-0.5 block text-[11px] text-muted-foreground">{timeAgo(n.created_at)}</span>
                </span>
              </button>
            ))
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
